import { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { Button } from "react-bootstrap";
import {
  getClaim,
  getRelatedClaims,
  updateClaim,
  getCurrentUser,
  listEntityRows,
  listUsers,
} from "../../services/apiClient.js";
import ClaimForm from "./ClaimForm.jsx";
import ClaimView from "./ClaimView.jsx";
import TabHeader from "./TabHeader.jsx";
import AcceptedStatusPanel from "./AcceptedStatusPanel.jsx";
import { CLAIM_FIELDS, CLAIM_LOOKUP_SOURCES, GATED_CLAIM_FIELDS, canEditGatedFields, emptyClaimForm } from "./claimFields.js";
import { toInputDate } from "../../utils/date.js";

// The read-view banner: the loud, glanceable claim states (legacy Details.aspx
// top row) — flags in red, the on-hold summary, callout QR state.
function ClaimBanner({ claim }) {
  const bits = [];
  if (claim.is_problem_job) bits.push(<span key="p" className="text-danger fw-semibold">Problem Job</span>);
  if (claim.is_important_job) bits.push(<span key="i" className="text-danger fw-semibold">Important Job</span>);
  if (claim.is_contractor_quoting) bits.push(<span key="cq" className="text-primary fw-semibold">Contractor Quoting</span>);

  const jobType = String(claim.job_type_name || "").trim().toLowerCase();
  if (jobType === "callout emergency works") {
    if (claim.qr_status === "received") bits.push(<span key="qr">Quote Request Received</span>);
    else if (claim.qr_status === "not_required") bits.push(<span key="qr">Quote Request Not Required</span>);
    else bits.push(<span key="qr" className="text-danger fw-semibold">Quote Request Not Set</span>);
  }

  if (claim.current_status_code === "A" && claim.accepted_status === "On Hold") {
    bits.push(
      <span key="oh" className="text-warning fw-semibold">
        On Hold — {claim.on_hold_reason || "no reason recorded"}
        {claim.on_hold_review_date ? ` · review ${String(claim.on_hold_review_date).slice(0, 10)}` : ""}
      </span>
    );
  }

  if (bits.length === 0) return null;
  return (
    <div className="d-flex flex-wrap column-gap-4 row-gap-1 border rounded px-3 py-2 mb-3">
      {bits}
    </div>
  );
}

// A job-number link with its type, as Karen Renall asked for on 2026-08-05: a
// related claim is usually the OTHER job at that address, and which one it is,
// Callout or R&Q, is the whole reason for clicking.
function ClaimLink({ r }) {
  return (
    <Link
      to={`/claims/${r.claim_number}`}
      data-hint={[r.client_name, r.street_address, r.status_name].filter(Boolean).join(" · ")}
    >
      {r.claim_number}
      {r.job_type_name ? <span className="text-muted small"> ({r.job_type_name})</span> : null}
    </Link>
  );
}

// Past this many links the row stops being a row and starts being a wall.
// Measured: 97% of cross-references are three claims or fewer, so the collapse
// only ever fires on the handful of big ones (job 73384 carries 164).
const RELATED_VISIBLE = 8;

// The legacy Details "Related Claims" row: MY CHILDREN PLUS MY PARENT, which is
// 1.0's rule exactly (Details.aspx.cs RelatedLinksHtml). Renders nothing when
// the claim has no cross-reference.
//
// Everything else on the cross-reference sits behind a disclosure. On job 73715
// the row is one link, as 1.0 shows it, and the other 163 claims on that body
// corporate are one click away instead of covering the screen (Robert, 15 Aug).
function RelatedClaims({ related, group }) {
  const [open, setOpen] = useState(false);
  const rows = related || [];
  const rest = group || [];
  if (!rows.length && !rest.length) return null;
  const shown = open ? rows : rows.slice(0, RELATED_VISIBLE);
  const hidden = rows.length - shown.length + rest.length;
  return (
    <div className="d-flex flex-wrap align-items-baseline column-gap-2 border rounded px-3 py-2 mb-3">
      <span className="text-muted small me-1">Related Claims:</span>
      {shown.map((r) => <ClaimLink key={r.id} r={r} />)}
      {open ? rest.map((r) => <ClaimLink key={r.id} r={r} />) : null}
      {hidden > 0 || open ? (
        <Button
          variant="link"
          size="sm"
          className="p-0 text-decoration-none small"
          onClick={() => setOpen((v) => !v)}
        >
          {open ? "▴ show less" : `▾ ${hidden} more on this cross-reference`}
        </Button>
      ) : null}
    </div>
  );
}

// View by default (a claim is data-heavy); Edit switches to the form.
function ClaimDetailsTab({ claimId, onChanged }) {
  const [claim, setClaim] = useState(null);
  const [form, setForm] = useState(emptyClaimForm);
  const [opts, setOpts] = useState({});
  const [users, setUsers] = useState([]);
  const [inspectors, setInspectors] = useState([]);
  const [related, setRelated] = useState({ related: [], group: [] });
  const [mode, setMode] = useState("view");
  const [me, setMe] = useState(null);
  const [error, setError] = useState("");
  const [saving, setSaving] = useState(false);
  const navigate = useNavigate();

  useEffect(() => {
    Promise.all(CLAIM_LOOKUP_SOURCES.map((k) => listEntityRows(k).then((r) => [k, r])))
      .then((pairs) => setOpts(Object.fromEntries(pairs)))
      .catch(() => {});
    listUsers({ pco: true }).then(setUsers).catch(() => {});
    // Inspector is its OWN list — see ClaimForm's PEOPLE_SOURCES.
    listUsers({ inspectors: true }).then(setInspectors).catch(() => {});
    getCurrentUser().then(setMe).catch(() => {});
    getClaim(claimId)
      .then(({ claim }) => setClaim(claim))
      .catch(() => setError("Unable to load claim."));
    getRelatedClaims(claimId)
      .then(setRelated)
      .catch(() => setRelated({ related: [], group: [] }));
  }, [claimId]);

  function startEdit() {
    const next = {};
    for (const field of CLAIM_FIELDS) {
      if (field.type === "date") {
        next[field.name] = toInputDate(claim[field.name]);
      } else {
        next[field.name] = claim[field.name] == null ? "" : String(claim[field.name]);
      }
    }
    setForm(next);
    setError("");
    setMode("edit");
  }

  function handleChange(event) {
    const { name, value, type, checked } = event.target;
    setForm((current) => ({ ...current, [name]: type === "checkbox" ? String(checked) : value }));
  }

  async function save() {
    setError("");
    setSaving(true);
    try {
      // Gated fields are server-enforced; strip them for non-privileged users so
      // an untouched disabled field can't trip the 403.
      const payload = { ...form };
      if (!canEditGatedFields(me)) for (const f of GATED_CLAIM_FIELDS) delete payload[f];
      const updated = await updateClaim(claimId, payload);
      setClaim(updated);
      setMode("view");
      if (onChanged) onChanged();
    } catch (err) {
      setError(err.response?.data?.error || "Unable to save changes.");
    } finally {
      setSaving(false);
    }
  }

  if (!claim) {
    return error ? (
      <div className="alert alert-danger">{error}</div>
    ) : (
      <p className="text-muted">Loading…</p>
    );
  }

  return (
    <>
      <TabHeader
        title="Claim Details"
        help="details"
        actions={
          mode === "view" ? (
            <>
              {/* Legacy Details "New Claim" = X-Ref. Labelled explicitly in 2.0
                  (Robert 2026-07-13) so nobody mistakes it for a blank claim —
                  search has the real New Claim button. */}
              <Button
                size="sm"
                variant="outline-primary"
                onClick={() => navigate(`/claims/new?xref=${claimId}`)}
                data-hint="Create a new claim pre-filled from this one, linked as a related claim"
              >
                New X-Ref Claim
              </Button>
              <Button size="sm" onClick={startEdit}>Edit</Button>
            </>
          ) : (
            <>
              <Button size="sm" onClick={save} disabled={saving}>Save</Button>
              <Button size="sm" variant="secondary" onClick={() => setMode("view")}>Cancel</Button>
            </>
          )
        }
      />
      {error ? <div className="alert alert-danger">{error}</div> : null}

      {mode === "view" ? <ClaimBanner claim={claim} /> : null}
      {mode === "view" ? <RelatedClaims related={related.related} group={related.group} /> : null}
      {claim.current_status_code === "A" ? (
        <AcceptedStatusPanel
          claim={claim}
          onChanged={() => {
            getClaim(claimId).then(({ claim: c }) => setClaim(c)).catch(() => {});
            if (onChanged) onChanged();
          }}
        />
      ) : null}

      {mode === "view" ? (
        <ClaimView claim={claim} />
      ) : (
        <>
          <ClaimForm
            form={form}
            onChange={handleChange}
            opts={opts}
            users={users}
            inspectors={inspectors}
            canEditGated={canEditGatedFields(me)}
            // The scheduling dates only render once the job HAS BEEN accepted,
            // and `form` cannot answer that — see claimFields `isAccepted`.
            statusCode={claim.current_status_code}
            wasAccepted={claim.ever_accepted === true}
          />
          <Button size="sm" onClick={save} disabled={saving}>Save</Button>
          <Button size="sm" variant="secondary" className="ms-2" onClick={() => setMode("view")}>
            Cancel
          </Button>
        </>
      )}
    </>
  );
}

export default ClaimDetailsTab;
